import { ExtraOptionsClear, ExtraOptionsUpdate } from "event-definitions"
import { useState } from "react"

/**
 * Custom hook to manage extra options for an `ExtendedSelect` component
 * when using a custom slot option.
 *
 * This hook listens for `ExtraOptionsUpdate` and `ExtraOptionsClear` events
 * that are linked to the given `selectId`. It keeps track of extra options,
 * where each key is an option id and the value is an array
 * with one object that has a `name` property.
 *
 * Required if using the `ExtendedSelect` component with a custom slot option.
 *
 * @param {string} selectId - The unique identifier for the select component instance.
 * @returns {{
 *   extraOptions: Record<string, Array<{ name: string }>>,
 *   setExtraOptions: React.Dispatch<React.SetStateAction<Record<string, Array<{ name: string }>>>>
 * }} An object containing the current extra options and a setter to manually update them.
 */
export function useExtraOptions(selectId) {
    const [extraOptions, setExtraOptions] = useState({})

    ExtraOptionsUpdate(selectId).useEvent(({ id, value }) => {
        const option = { name: value }
        setExtraOptions((prevOptions) => ({
            ...prevOptions,
            [id]: [option],
        }))
    })

    ExtraOptionsClear(selectId).useEvent(() => {
        setExtraOptions({})
    })

    return {
        extraOptions,
        setExtraOptions,
    }
}
